import * as React from 'react'
import { cn } from '@/lib/utils'
import { HugeiconsIcon } from '@hugeicons/react'
import type { IconSvgElement } from '@hugeicons/react'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean
  icon?: IconSvgElement
  'aria-describedby'?: string
  'aria-invalid'?: boolean
}

// 44px `h-11 rounded-lg` is DELIBERATE, not drift from the app's `.input` (32px) /
// `.input-lg` (40px) tiers: this component is the onboarding wizard's field (StepWelcome,
// StepProject, StepInviteTeam), a conversion surface that keeps its taller tier the same way
// the button work did. Decided 2026-09-22 (user_decisions_log); AuthCard keeps 48px for the
// same reason. Do not migrate it onto `.input-lg` without a redesign that means to.
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, error, icon, ...props }, ref) => {
    if (icon) {
      return (
        <div className="relative">
          <HugeiconsIcon
            icon={icon}
            size={16}
            strokeWidth={2}
            color="currentColor"
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
            aria-hidden="true"
          />
          <input
            type={type}
            className={cn(
              'flex h-11 w-full rounded-lg border bg-white pl-9 pr-4 py-2 text-base transition-colors',
              '',
              'focus:outline-none focus-glow',
              'disabled:cursor-not-allowed disabled:opacity-50',
              error
                ? 'border-red-500 focus:border-red-500 [--focus-glow-rgb:239,68,68]'
                : 'border-gray-200 hover:border-gray-400 focus:border-primary-500',
              className
            )}
            ref={ref}
            {...props}
          />
        </div>
      )
    }

    return (
      <input
        type={type}
        className={cn(
          'flex h-11 w-full rounded-lg border bg-white px-4 py-2 text-base transition-colors',
          '',
          'focus:outline-none focus-glow',
          'disabled:cursor-not-allowed disabled:opacity-50',
          error
            ? 'border-red-500 focus:border-red-500 [--focus-glow-rgb:239,68,68]'
            : 'border-gray-200 hover:border-gray-400 focus:border-primary-500',
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)

Input.displayName = 'Input'

export { Input }
